import axios from 'axios';
import { getApiUrl, getFileUrl } from '../utils/config'; // Import getApiUrl
import { API_URL, handleApiError } from '../utils/config';
const API = `${API_URL}/orders`;
// Helper function to get auth headers (optional for guest checkout)
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  
  // If no token is available, return empty headers for guest checkout
  if (!token) {
    return {};
  }
  
  // Return auth headers for logged-in users
  return {
    headers: {
      'x-auth-token': token,
      'Authorization': `Bearer ${token}` // Keep for backwards compatibility
    }
  };
};
// Get all orders for current authenticated user
export const getUserOrders = async () => {
  try {
    const response = await axios.get(`${API}/my-orders`, getAuthHeaders());
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Helper to extract Stripe session ID from URL if present
const getStripeSessionId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('session_id') || urlParams.get('stripe_session');
  
  // If no session ID in URL params, check if it's in the path (Stripe redirects sometimes include it in the path)
  if (!sessionId) {
    // Extract from path if it matches a pattern like /success/cs_test_...
    const pathMatch = window.location.pathname.match(/\/success\/(cs_[a-zA-Z0-9_]+)/);
    if (pathMatch && pathMatch[1]) {
      return pathMatch[1];
    }
    
    // Also check localStorage for a saved Stripe session ID
    const savedStripeSession = localStorage.getItem('stripe_session_id');
    if (savedStripeSession) {
      return savedStripeSession;
    }
  }
  
  // If we found a session ID, save it to localStorage for future use
  if (sessionId) {
    localStorage.setItem('stripe_session_id', sessionId);
  }
  
  return sessionId;
};

// Get a specific order by ID (supports both authenticated users and guests)
export const getOrderById = async (orderId) => {
  try {
    // Guest ownership is sent in a header so the cart capability does not enter
    // browser history or reverse-proxy URL logs.
    let url = `${API}/${orderId}`;
    const headers = getAuthHeaders();
    const queryParams = [];
    
    // Check for Stripe session ID in the URL (from payment link)
    const stripeSessionId = getStripeSessionId();
    if (stripeSessionId) {
      // Add as query parameter
      queryParams.push(`stripe_session=${stripeSessionId}`);
      
      // Also add as header for maximum compatibility
      headers.headers = headers.headers || {};
      headers.headers['x-stripe-session'] = stripeSessionId;
      
    }
    
    // If this is a guest user, add the session ID
    if (!localStorage.getItem('token')) {
      const sessionId = localStorage.getItem('cart_session_id');
      if (sessionId) {
        headers.headers = headers.headers || {};
        headers.headers['x-session-id'] = sessionId;
      }
    }
    
    // Add query parameters to URL if any exist
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    const response = await axios.get(url, headers);
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Update status for multiple orders
export const updateBulkOrderStatus = async (orderIds, status_id) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.put(
      getApiUrl('/api/orders/admin/bulk-status'), // Corrected path
      { orderIds, status_id },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data; // Should return { message, updatedCount }
  } catch (error) {
    console.error('Error updating bulk order status:', error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to update bulk order status');
  }
};
// Create a new order (supports guest checkout)
export const createOrder = async (orderData) => {
  try {
    // First check if there are items in the cart
    try {
      const { getCartItems } = require('./cartService');
      const cartResponse = await getCartItems();
      
      if (!cartResponse || !cartResponse.cartItems || cartResponse.cartItems.length === 0) {
        throw new Error("Your cart is empty. Please add items to your cart before checking out.");
      }
      
    } catch (cartError) {
      console.error("Error checking cart items:", cartError);
      // Continue anyway since the error might be unrelated to empty cart
    }
    
    // Add session ID for guest orders if available
    if (!localStorage.getItem('token')) {
      // First try the global window property (fallback mechanism)
      let sessionId = window.__cart_session_id;

      // If that fails, try localStorage
      if (!sessionId) {
        sessionId = localStorage.getItem('cart_session_id');
      }

      // If we still don't have a session ID, create one
      if (!sessionId) {
        // If no cart session exists, create one
        sessionId = `guest-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
        localStorage.setItem('cart_session_id', sessionId);
        window.__cart_session_id = sessionId;
      }

      orderData.session_id = sessionId;
    }

    // Check for recently clicked notification (for conversion tracking)
    try {
      const lastClickStr = localStorage.getItem('last_notification_click');
      if (lastClickStr) {
        const lastClick = JSON.parse(lastClickStr);
        // Check if click is within 30 minutes
        if (lastClick.expiresAt && Date.now() < lastClick.expiresAt) {
          orderData.clicked_notification_id = lastClick.notificationId;
          console.log('📊 Linking order to notification click:', lastClick.notificationId);
        } else {
          // Expired, remove it
          localStorage.removeItem('last_notification_click');
        }
      }
    } catch (e) {
      console.log('Could not retrieve notification click data:', e);
    }

    const response = await axios.post(API, orderData, getAuthHeaders());
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Update an existing order
export const updateOrder = async (orderId, orderData) => {
  try {
    const response = await axios.put(`${API}/${orderId}`, orderData, getAuthHeaders());
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Delete an order
export const deleteOrder = async (orderId) => {
  try {
    const response = await axios.delete(`${API}/${orderId}`, getAuthHeaders());
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Admin-specific functions
// Get all orders (admin only), accepts optional query parameters
export const getAllOrders = async (params = {}) => { // Accept params object
  try {
    // Make sure we're passing the correct parameters for filtering and sorting
    const config = {
      ...getAuthHeaders(), // Get auth headers
      params: params       // Add query parameters
    };
    
    // Log the parameters being used for debugging
    // console.log('Getting orders with params:', params);
    
    const response = await axios.get(`${API}/admin/all`, config); // Pass config with params
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Get admin version of order by ID
export const getAdminOrderById = async (orderId) => {
  try {
    const response = await axios.get(`${API}/admin/${orderId}`, getAuthHeaders());
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Update order status (admin only)
export const updateOrderStatus = async (orderId, statusId) => {
  try {
    const response = await axios.put(
      `${API}/admin/${orderId}/status`, 
      { status_id: statusId }, 
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Update order shipping info (admin only)
export const updateOrderShipping = async (orderId, shippingInfo) => {
  try {
    const response = await axios.put(
      `${API}/admin/${orderId}/shipping`, 
      shippingInfo, 
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Update order notes (admin only)
export const updateOrderNotes = async (orderId, notes) => {
  try {
    const response = await axios.put(
      `${API}/admin/${orderId}/notes`,
      { order_notes: notes },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Update phone number (admin only)
export const updateOrderPhone = async (orderId, phoneNumber) => {
  try {
    const response = await axios.put(
      `${API}/admin/${orderId}/phone`,
      { phonenumber: phoneNumber },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Update email (admin only)
export const updateOrderEmail = async (orderId, email) => {
  try {
    const response = await axios.put(
      `${API}/admin/${orderId}/email`,
      { email: email },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Generate Omniva barcode for an order (admin only)
export const generateOmnivaBarcode = async (orderId, terminalId, country = 'LV', force = false) => {
  try {
    const response = await axios.post(
      `${API}/admin/${orderId}/omniva/barcode`,
      { terminalId, country, force },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Generate Omniva label for an order with existing barcode (admin only)
export const generateOmnivaLabel = async (orderId) => {
  try {
    const response = await axios.post(
      `${API}/admin/${orderId}/omniva/label`,
      {},
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Delete Omniva label file from the server after it's been downloaded (admin only)
export const deleteOmnivaLabel = async (orderId, labelPath) => {
  try {
    const response = await axios.delete(
      `${API}/admin/${orderId}/omniva/label`,
      {
        ...getAuthHeaders(),
        data: { labelPath }
      }
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    
    // We don't throw here to prevent disrupting the user experience if deletion fails
    return { success: false, message: errorMessage };
  }
};

// Generate a bulk PDF for multiple orders (admin only)
export const generateBulkOrderPdf = async (orderIds) => {
  try {
    const response = await axios.post(
      `${API}/admin/bulk-pdf`,
      { orderIds },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Generate bulk Omniva barcodes for multiple orders (admin only)
// If terminalId is null, backend will use the existing terminal IDs from the orders
export const generateBulkOmnivaBarcodes = async (orderIds, terminalId = null, country = 'LV') => {
  try {
    const response = await axios.post(
      `${API}/admin/bulk-omniva-barcodes`,
      { orderIds, terminalId, country },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Generate bulk Omniva labels for multiple orders (admin only)
export const generateBulkOmnivaLabels = async (orderIds) => {
  try {
    const response = await axios.post(
      `${API}/admin/bulk-omniva-labels`,
      { orderIds },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Generate PDF of all products in processing status (admin only)
export const generateProcessingProductsPdf = async () => {
  try {
    // Use only the direct endpoint since it's working reliably
    const response = await axios.get(
      `${API_URL}/direct-processing-pdf`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('PDF generation endpoint failed:', error);
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
